import { Injectable } from '@angular/core';
import { Observable, SubscriptionLike } from 'rxjs';
import { XSightHttpService, AppSettingsService, DEVICE_OS_FAMILY, hd } from '@nx-fe/services';
import { RemoteControlDeviceState, DeviceStateRegistryService } from './device-state-registry.service';
import { RcDeviceService } from './rc-device-service';
import { IWorkPanelWindow } from './work-panel.service';
import { RemoteControlSessionService } from './session.service';

@Injectable({
  providedIn: 'root',
})
export class TunnelService {
  private $window: IWorkPanelWindow = window as any;

  public linuxTunnelServiceAvailableAgentVersion = '14.3.0';

  public runCommandPromptPermissionMCVersion = '15.2.0'; // Only starting with MC version 15.2, SA agent can restrict access to ShellTerminal/WebConsole

  constructor(
    private http: XSightHttpService,
    private appSettingsService: AppSettingsService,
    private deviceStateRegistry: DeviceStateRegistryService,
    private remoteControlSessionService: RemoteControlSessionService,
    private deviceService: RcDeviceService
  ) { }

  /**
   ** Sends the request and returns an observable that popOutWebConsole and popOutTerminal
   ** can use to launch window once the first request is complete
   ** @param deviceId The device ID
   ** @return Observable for the HeaderToken request
   */
  public sendHeaderToken = (deviceId: string): Observable<string> => {
    return this.http.get<string>(
      this.appSettingsService.accountServiceUrl + 'tunnel?deviceId=' + deviceId
    );
  };

  public isLinuxDevice = (rcState: RemoteControlDeviceState): boolean => {
    return this.deviceService.isSpecificDeviceFamily(rcState, [DEVICE_OS_FAMILY.Linux]);
  };

  public isTizenDevice = (rcState: RemoteControlDeviceState): boolean => {
    return this.deviceService.isSpecificDeviceFamily(rcState, [DEVICE_OS_FAMILY.Tizen]);
  };

  public popOutWebConsole = (rcState: RemoteControlDeviceState): SubscriptionLike => {
    console.log('Sending session establishment request');

    const tserviceUrl = this.getLinuxTunnelServiceWebConsoleUrl(rcState);
    const result = this.sendHeaderToken(this.getLinuxDeviceId(rcState));

    return result.subscribe(
      () => {
        console.log('Header token updated. Launching popup Web Console..');
        this.$window.open(
          tserviceUrl,
          '_blank',
          'scrollbars=no, fullscreen=yes, width=' + (screen.availWidth - 15) + ',height=' + screen.availHeight
        );
      },
      (error: string) => {
        console.log(error);
      }
    );
  };

  public popOutTerminal = (rcState: RemoteControlDeviceState): SubscriptionLike => {
    console.log('Sending session establishment request');

    const tserviceUrl = this.getLinuxTunnelServiceTerminalUrl(rcState);
    const result = this.sendHeaderToken(this.getLinuxDeviceId(rcState));

    return result.subscribe(
      () => {
        console.log('Header token updated. Launching popup Terminal..');
        this.$window.open(
          tserviceUrl,
          '_blank',
          'scrollbars=no, fullscreen=yes, width=' + (screen.availWidth - 15) + ',height=' + screen.availHeight
        );
      },
      (error: string) => {
        console.log(error);
      }
    );
  };

  public allowRemoteControlTerminal = (rcState: RemoteControlDeviceState) => {
    if (
      (this.deviceStateRegistry.anyRcConnected() &&
        this.isLinuxDevice(rcState) &&
        this.remoteControlSessionService.compareAgentVersion(this.linuxTunnelServiceAvailableAgentVersion) >= 0) ||
      this.isTizenDevice(rcState)
    ) {
      if (this.remoteControlSessionService.compareMcVersion(this.runCommandPromptPermissionMCVersion) < 0) {
        return true;
      }
      return this.deviceStateRegistry.hasPermission(rcState, hd.RemoteControlPermissions.RemoteControlCommands);
    }
    return false;
  };

  public allowRemoteControlWebConsole = (rcState: RemoteControlDeviceState) => {
    if (rcState && rcState.connected && this.isLinuxDeviceButtonsAvailable(rcState) && this.isLinuxDevice) {
      if (this.remoteControlSessionService.compareMcVersion(this.runCommandPromptPermissionMCVersion) < 0) {
        return true;
      }
      return this.deviceStateRegistry.hasPermission(rcState, hd.RemoteControlPermissions.RemoteControlWebConsole);
    }
    return false;
  };

  private isLinuxDeviceButtonsAvailable = (rcState: RemoteControlDeviceState): boolean => {
      const result = this.isLinuxTunnelServiceAvailable(rcState) && this.deviceStateRegistry.anyRcConnected();
      return result;
    };

  private isLinuxTunnelServiceAvailable = (rcState: RemoteControlDeviceState): boolean => {
    const result =
      this.isLinuxDevice(rcState) &&
      this.remoteControlSessionService.compareAgentVersion(this.linuxTunnelServiceAvailableAgentVersion) >= 0;
    return result;
  };

  private getLinuxTunnelServiceTerminalUrl = (rcState: RemoteControlDeviceState): string => {
    let deviceId = this.getLinuxDeviceId(rcState);
    return this.appSettingsService.linuxTunnelServiceTerminalUrl.replace(/{deviceid}/gi, deviceId);
  };

  private getLinuxTunnelServiceWebConsoleUrl = (rcState: RemoteControlDeviceState): string => {
    let deviceId = this.getLinuxDeviceId(rcState);
    return this.appSettingsService.linuxTunnelServiceWebConsoleUrl.replace(/{deviceid}/gi, deviceId);
  };

  private getLinuxDeviceId = (rcState: RemoteControlDeviceState): string => {
    return this.isLinuxDevice(rcState) || this.isTizenDevice(rcState) ? rcState.device.deviceId : '';
  };

}
